import type { IncomeBoat } from '@sea-snag/shared';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sea-snag/ui';

import { type IncomeFilters, NO_FILTERS, type OwnershipFilter } from '../../lib/income';

const OWNERSHIP: Array<{ value: OwnershipFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'company', label: 'Company-owned' },
  { value: 'external', label: 'External owner' },
];

/** Boat, owner and ownership-type filters, applied together to everything on the page (#217). */
export function IncomeFiltersBar({
  boats,
  filters,
  onChange,
  included,
}: {
  /** Every boat of the season, for the owner list. */
  boats: readonly IncomeBoat[];
  filters: IncomeFilters;
  onChange: (f: IncomeFilters) => void;
  /** How many boats the filters keep. */
  included: number;
}) {
  const owners = [
    ...new Map(boats.flatMap((b) => (b.owner ? [[b.owner.pid, b.owner.name] as const] : []))),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const filtered =
    filters.search !== '' || filters.ownerPid !== 'all' || filters.ownership !== 'all';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        aria-label="Search boats"
        placeholder="Search boats"
        className="h-8 w-48 text-sm"
        value={filters.search}
        onChange={(e) => onChange({ ...filters, search: e.target.value })}
      />
      <Select
        value={filters.ownerPid}
        onValueChange={(ownerPid) => onChange({ ...filters, ownerPid })}
      >
        <SelectTrigger className="h-8 w-48 text-sm" aria-label="Owner">
          <SelectValue placeholder="All owners" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All owners</SelectItem>
          {owners.map(([pid, name]) => (
            <SelectItem key={pid} value={pid}>
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex rounded-md border" role="group" aria-label="Ownership type">
        {OWNERSHIP.map((o) => (
          <Button
            key={o.value}
            variant={filters.ownership === o.value ? 'default' : 'ghost'}
            size="sm"
            className="h-8 rounded-none px-3 text-xs first:rounded-l-md last:rounded-r-md"
            aria-pressed={filters.ownership === o.value}
            onClick={() => onChange({ ...filters, ownership: o.value })}
          >
            {o.label}
          </Button>
        ))}
      </div>
      <span className="text-xs text-muted-foreground">
        {included} of {boats.length} boats
      </span>
      {filtered && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-xs"
          onClick={() => onChange(NO_FILTERS)}
        >
          Reset filters
        </Button>
      )}
    </div>
  );
}
